import { useMemo, type ReactNode } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';

import Dropdown from '@cartwhl/ui/atoms/Dropdown';
import Text from '@cartwhl/ui/base/Text';
import ChevronRightIcon from '@cartwhl/ui/icons/ChevronRightIcon';
import CrossIcon from '@cartwhl/ui/icons/CrossIcon';
import MinusIcon from '@cartwhl/ui/icons/MinusIcon';
import PlusIcon from '@cartwhl/ui/icons/PlusIcon';

import {
  buildVisibleChainGroups,
  type ChainMenuGroup,
} from '../../../lib/schema/navigation';
import ChainPill from './ChainPill';

const SIDE_SPLIT_GROUP_KEYS = new Set(['fingers']);

interface ChainOverrideControlProps {
  chainName: string | null;
  presentChainNames: ReadonlySet<string>;
  pendingChainPathSelection: {
    chainName: string;
    phase: 'pick-start' | 'pick-end';
    startBoneRef: string | null;
  } | null;
  onTriggerSelectBone?: () => void;
  onClearChain?: () => void;
  onBeginStartEnd: (chainName: string) => void;
  onCancelStartEnd?: () => void;
  onAddToChain: (chainName: string) => void;
  onRemoveFromChain?: () => void;
}

interface ChainActionSubmenuProps {
  title: string;
  icon?: ReactNode;
  groups: ChainMenuGroup[];
  disabled?: boolean;
  currentChainName: string | null;
  presentChainNames: ReadonlySet<string>;
  onSelectChain: (chainName: string) => void;
}

interface ChainGroupSubmenuProps {
  label: string;
  chains: readonly string[];
  currentChainName: string | null;
  presentChainNames: ReadonlySet<string>;
  onSelectChain: (chainName: string) => void;
}

export default function ChainOverrideControl({
  chainName,
  presentChainNames,
  pendingChainPathSelection,
  onTriggerSelectBone,
  onClearChain,
  onBeginStartEnd,
  onCancelStartEnd,
  onAddToChain,
  onRemoveFromChain,
}: ChainOverrideControlProps) {
  const visibleGroups = useMemo<ChainMenuGroup[]>(
    () => buildVisibleChainGroups(presentChainNames),
    [presentChainNames],
  );
  const addToChainGroups = useMemo(
    () =>
      visibleGroups
        .map((group) => ({
          ...group,
          chains: group.chains.filter((groupChainName) =>
            presentChainNames.has(groupChainName),
          ),
        }))
        .filter((group) => group.chains.length > 0),
    [presentChainNames, visibleGroups],
  );

  const stopPropagation = (event: { stopPropagation: () => void }) => {
    event.stopPropagation();
  };

  return (
    <Dropdown
      inModal
      align="start"
      side="bottom"
      width={288}
      trigger={
        <button
          type="button"
          className="group dark:hover:bg-gray-90/40 hover:bg-gray-15/70 flex w-full min-w-0 rounded-md px-1 py-0.5 text-left transition-colors"
          onPointerDown={(event) => {
            onTriggerSelectBone?.();
            stopPropagation(event);
          }}
          onClick={stopPropagation}
          aria-label={chainName ? `Chain ${chainName}. Click to update` : 'Assign chain'}
        >
          <span className="min-w-0 flex-1">
            {chainName ? (
              <ChainPill chainName={chainName} />
            ) : (
              <span className="flex h-4 items-center justify-center">
                <PlusIcon className="text-gray-60 dark:text-gray-45 h-3.5 w-3.5 opacity-0 transition-opacity group-hover:opacity-100" />
              </span>
            )}
          </span>
        </button>
      }
    >
      <Dropdown.Item
        onClick={onClearChain}
        disabled={!chainName || !onClearChain}
        icon={<CrossIcon className="text-gray-60 dark:text-gray-45 h-3.5 w-3.5" />}
      >
        Clear Chain
      </Dropdown.Item>
      {pendingChainPathSelection && onCancelStartEnd && (
        <Dropdown.Item onClick={onCancelStartEnd}>
          Cancel Start &amp; End mode
        </Dropdown.Item>
      )}
      <Dropdown.Item
        onClick={onRemoveFromChain}
        disabled={!chainName || !onRemoveFromChain}
        icon={<MinusIcon className="text-gray-60 dark:text-gray-45 h-3.5 w-3.5" />}
      >
        Remove from Chain
      </Dropdown.Item>
      <ChainActionSubmenu
        title="Add to Chain"
        icon={<PlusIcon className="text-gray-60 dark:text-gray-45 h-3.5 w-3.5" />}
        groups={addToChainGroups}
        disabled={addToChainGroups.length === 0}
        currentChainName={chainName}
        presentChainNames={presentChainNames}
        onSelectChain={onAddToChain}
      />
      <Dropdown.Separator />
      <ChainActionSubmenu
        title="Create Chain"
        groups={visibleGroups}
        currentChainName={chainName}
        presentChainNames={presentChainNames}
        onSelectChain={onBeginStartEnd}
      />
    </Dropdown>
  );
}

function ChainActionSubmenu({
  title,
  icon,
  groups,
  disabled = false,
  currentChainName,
  presentChainNames,
  onSelectChain,
}: ChainActionSubmenuProps) {
  return (
    <DropdownMenu.Sub>
      <DropdownMenu.SubTrigger
        disabled={disabled}
        className="dark:hover:bg-gray-90 hover:bg-gray-15 data-[state=open]:bg-gray-15 dark:data-[state=open]:bg-gray-90 data-[disabled]:pointer-events-none data-[disabled]:opacity-50 outline-hidden flex w-full items-center rounded-lg py-2.5 pl-3 pr-3 transition-colors"
      >
        {icon && <span className="mr-3 w-5">{icon}</span>}
        <Text variant="studio/body/medium/sm">{title}</Text>
        <ChevronRightIcon className="text-gray-45 ml-auto" />
      </DropdownMenu.SubTrigger>
      <DropdownMenu.Portal>
        <DropdownMenu.SubContent
          sideOffset={8}
          alignOffset={-8}
          collisionPadding={8}
          className="RxSubContent text-gray-90 dark:bg-gray-75 shadow-menu z-20 min-w-[196px] rounded-2xl bg-white p-2 dark:text-white"
        >
          {groups.map((group) =>
            SIDE_SPLIT_GROUP_KEYS.has(group.key) ? (
              <SideSplitChainGroupSubmenu
                key={group.key}
                label={group.label}
                chains={group.chains}
                currentChainName={currentChainName}
                presentChainNames={presentChainNames}
                onSelectChain={onSelectChain}
              />
            ) : (
              <ChainGroupSubmenu
                key={group.key}
                label={group.label}
                chains={group.chains}
                currentChainName={currentChainName}
                presentChainNames={presentChainNames}
                onSelectChain={onSelectChain}
              />
            ),
          )}
        </DropdownMenu.SubContent>
      </DropdownMenu.Portal>
    </DropdownMenu.Sub>
  );
}

function ChainGroupSubmenu({
  label,
  chains,
  currentChainName,
  presentChainNames,
  onSelectChain,
}: ChainGroupSubmenuProps) {
  return (
    <DropdownMenu.Sub>
      <DropdownMenu.SubTrigger className="dark:hover:bg-gray-90 hover:bg-gray-15 data-[state=open]:bg-gray-15 dark:data-[state=open]:bg-gray-90 outline-hidden flex w-full items-center rounded-lg py-2.5 pl-3 pr-3 transition-colors">
        <Text variant="studio/body/medium/sm">{label}</Text>
        <ChevronRightIcon className="text-gray-45 ml-auto" />
      </DropdownMenu.SubTrigger>
      <DropdownMenu.Portal>
        <DropdownMenu.SubContent
          sideOffset={8}
          alignOffset={-8}
          collisionPadding={8}
          className="RxSubContent text-gray-90 dark:bg-gray-75 shadow-menu z-20 min-w-[196px] rounded-2xl bg-white p-2 dark:text-white"
        >
          <Text as="div" variant="studio/body/medium/sm">
            <div className="max-h-64 overflow-y-auto">
              {chains.map((chain) => (
                <ChainOptionItem
                  key={chain}
                  chainName={chain}
                  currentChainName={currentChainName}
                  presentChainNames={presentChainNames}
                  onSelectChain={onSelectChain}
                />
              ))}
            </div>
          </Text>
        </DropdownMenu.SubContent>
      </DropdownMenu.Portal>
    </DropdownMenu.Sub>
  );
}

function SideSplitChainGroupSubmenu({
  label,
  chains,
  currentChainName,
  presentChainNames,
  onSelectChain,
}: ChainGroupSubmenuProps) {
  const leftChains = chains.filter((chain) => chain.startsWith('Left'));
  const rightChains = chains.filter((chain) => chain.startsWith('Right'));
  const centerChains = chains.filter(
    (chain) => !chain.startsWith('Left') && !chain.startsWith('Right'),
  );

  return (
    <DropdownMenu.Sub>
      <DropdownMenu.SubTrigger className="dark:hover:bg-gray-90 hover:bg-gray-15 data-[state=open]:bg-gray-15 dark:data-[state=open]:bg-gray-90 outline-hidden flex w-full items-center rounded-lg py-2.5 pl-3 pr-3 transition-colors">
        <Text variant="studio/body/medium/sm">{label}</Text>
        <ChevronRightIcon className="text-gray-45 ml-auto" />
      </DropdownMenu.SubTrigger>
      <DropdownMenu.Portal>
        <DropdownMenu.SubContent
          sideOffset={8}
          alignOffset={-8}
          collisionPadding={8}
          className="RxSubContent text-gray-90 dark:bg-gray-75 shadow-menu z-20 min-w-[196px] rounded-2xl bg-white p-2 dark:text-white"
        >
          {leftChains.length > 0 && (
            <ChainGroupSubmenu
              label="Left"
              chains={leftChains}
              currentChainName={currentChainName}
              presentChainNames={presentChainNames}
              onSelectChain={onSelectChain}
            />
          )}
          {rightChains.length > 0 && (
            <ChainGroupSubmenu
              label="Right"
              chains={rightChains}
              currentChainName={currentChainName}
              presentChainNames={presentChainNames}
              onSelectChain={onSelectChain}
            />
          )}
          {centerChains.length > 0 && (
            <Text as="div" variant="studio/body/medium/sm">
              <div className="max-h-64 overflow-y-auto">
                {centerChains.map((chain) => (
                  <ChainOptionItem
                    key={chain}
                    chainName={chain}
                    currentChainName={currentChainName}
                    presentChainNames={presentChainNames}
                    onSelectChain={onSelectChain}
                  />
                ))}
              </div>
            </Text>
          )}
        </DropdownMenu.SubContent>
      </DropdownMenu.Portal>
    </DropdownMenu.Sub>
  );
}

interface ChainOptionItemProps {
  chainName: string;
  currentChainName: string | null;
  presentChainNames: ReadonlySet<string>;
  onSelectChain: (chainName: string) => void;
}

function ChainOptionItem({
  chainName,
  currentChainName,
  presentChainNames,
  onSelectChain,
}: ChainOptionItemProps) {
  const isActive = chainName === currentChainName;
  const isPresent = presentChainNames.has(chainName);
  return (
    <Dropdown.Item
      onClick={() => onSelectChain(chainName)}
      className={`${isActive ? 'bg-blue hover:bg-blue text-white' : ''} [&>button>span:last-child]:block [&>button>span:last-child]:min-w-0 [&>button>span:last-child]:flex-1`}
      icon={
        <span
          className={`mt-0.5 inline-flex h-2 w-2 rounded-full ${
            isPresent
              ? isActive
                ? 'bg-white'
                : 'bg-blue-50'
              : 'bg-transparent'
          }`}
          aria-hidden
        />
      }
    >
      <span className="block min-w-0">
        <ChainPill
          chainName={chainName}
          className="min-w-[96px]"
          textVariant="studio/body/medium/sm"
        />
      </span>
    </Dropdown.Item>
  );
}
